import {
  ArrowRight,
  BrainCircuit,
  Check,
  CircleDot,
  Clock,
  FileCheck2,
  Gauge,
  Mail,
  Megaphone,
  ShieldCheck,
  ShoppingCart,
  Users,
  Workflow,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BrandMark } from '../../components/BrandMark'

const outcomes = [
  {
    title: 'Stops work being dropped',
    copy: 'DAWN keeps track of repeat jobs, deadlines and follow-ups so fewer things depend on you remembering them.',
    icon: Workflow,
  },
  {
    title: 'Prepares work before you start',
    copy: 'It can gather the right information and prepare a first draft, checklist or recommendation for you to review.',
    icon: BrainCircuit,
  },
  {
    title: 'Helps you follow up',
    copy: 'It can prepare customer replies, sales follow-ups and meeting notes without sending anything you have not approved.',
    icon: Mail,
  },
  {
    title: 'Gives you time back',
    copy: 'The aim is simple: less chasing, copying, searching and starting from scratch every day.',
    icon: Clock,
  },
]

const helpAreas = [
  {
    number: '01',
    title: 'Marketing and content',
    copy: 'DAWN can turn your approved business information into useful first drafts and organised campaigns.',
    points: ['Social and email draft preparation', 'Product and service descriptions', 'Campaign ideas and content plans'],
    icon: Megaphone,
  },
  {
    number: '02',
    title: 'Sales and customer follow-up',
    copy: 'DAWN can prepare the information and wording your team needs to respond more quickly and consistently.',
    points: ['Lead and company research', 'Meeting preparation and notes', 'Follow-up and customer reply drafts'],
    icon: Users,
  },
  {
    number: '03',
    title: 'Business admin and oversight',
    copy: 'DAWN can watch recurring jobs, surface problems and give you one clear list of what needs attention.',
    points: ['Daily or weekly priority summaries', 'Missed-task and exception alerts', 'Approval and decision lists'],
    icon: ShoppingCart,
  },
]

const money = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
})

function PilotPreview() {
  return (
    <div className="dawn-preview" aria-label="Example DAWN business dashboard">
      <div className="dawn-preview__bar">
        <div><span /><span /><span /></div>
        <small>Example business dashboard</small>
      </div>
      <div className="dawn-preview__shell">
        <aside>
          <BrandMark compact light />
          {['Home', 'Jobs to check', 'Business information', 'Automations', 'Approvals'].map((item, index) => (
            <span className={index === 0 ? 'active' : ''} key={item}>{item}</span>
          ))}
        </aside>
        <div className="dawn-preview__canvas">
          <header>
            <div>
              <small>Your business today</small>
              <strong>What needs your attention?</strong>
            </div>
            <button type="button">Review list</button>
          </header>
          <div className="dawn-preview__metrics">
            <article><small>Jobs being handled</small><strong>12</strong><span>Across marketing, sales and admin</span></article>
            <article><small>Waiting for you</small><strong>3</strong><span>Important decisions only</span></article>
            <article><small>Time freed this week</small><strong>18h</strong><span>Illustrative example</span></article>
          </div>
          <div className="dawn-preview__grid">
            <article className="dawn-preview__queue">
              <small>Prepared for review</small>
              <span><i />Customer reply draft</span>
              <span><i />Next week’s content plan</span>
              <span><i />Sales meeting brief</span>
            </article>
            <article className="dawn-preview__status">
              <small>You stay in control</small>
              <span><i />Nothing sent without approval</span>
              <span><i />Spending locked</span>
              <span><i />Pause at any time</span>
            </article>
          </div>
        </div>
      </div>
    </div>
  )
}

function SavingsEstimator() {
  const [hoursPerWeek, setHoursPerWeek] = useState(15)
  const [hourlyValue, setHourlyValue] = useState(30)
  const [reduction, setReduction] = useState(40)

  const monthlyHours = Math.round(hoursPerWeek * 4.33 * (reduction / 100))
  const monthlyValue = Math.round(monthlyHours * hourlyValue)
  const annualValue = monthlyValue * 12

  return (
    <div className="savings-estimator">
      <div className="savings-estimator__intro">
        <span className="eyebrow">Thinking of expanding?</span>
        <h2>See what DAWN could save you before you add more overhead.</h2>
        <p>Hiring is sometimes the right answer. But if the pressure comes from repetitive admin, chasing, research or first-draft work, DAWN may help your current team handle more before you recruit.</p>
        <div className="savings-context">
          <article><Users size={21} /><div><strong>Hiring adds capacity</strong><span>But also salary, recruitment, equipment, training and management time.</span></div></article>
          <article><Workflow size={21} /><div><strong>DAWN targets repeat work</strong><span>Start with one problem, measure the result and expand only when it proves useful.</span></div></article>
        </div>
      </div>
      <div className="savings-estimator__calculator">
        <h3>Your rough time-saving estimate</h3>
        <label>
          Hours spent on repeat work each week
          <input type="number" min="1" max="200" value={hoursPerWeek} onChange={event => setHoursPerWeek(Number(event.target.value) || 0)} />
        </label>
        <label>
          Approximate value of that time per hour
          <div className="money-input"><span>£</span><input type="number" min="1" max="1000" value={hourlyValue} onChange={event => setHourlyValue(Number(event.target.value) || 0)} /></div>
        </label>
        <label>
          Estimated share DAWN could reduce
          <input type="range" min="10" max="70" step="5" value={reduction} onChange={event => setReduction(Number(event.target.value))} />
          <span className="range-value">{reduction}%</span>
        </label>
        <div className="savings-estimator__results" aria-live="polite">
          <article><small>Hours freed each month</small><strong>{monthlyHours}</strong></article>
          <article><small>Estimated monthly value</small><strong>{money.format(monthlyValue)}</strong></article>
          <article><small>Estimated annual value</small><strong>{money.format(annualValue)}</strong></article>
        </div>
        <p className="estimate-note">Illustrative estimate only. Actual savings depend on the work selected, your team, setup quality and how consistently the workflow is used.</p>
        <Link className="button button--gold" to="/request-demo">Join the first test cohort <ArrowRight size={17} /></Link>
      </div>
    </div>
  )
}

export function HomePage() {
  return (
    <>
      <section className="conversion-hero">
        <div className="conversion-hero__horizon" />
        <div className="container conversion-hero__grid">
          <div className="conversion-hero__copy">
            <span className="eyebrow">Savings, capability and opportunity</span>
            <h1>Start with the outcome. DAWN works out the technology.</h1>
            <p>Tell us what you want to improve. We identify the saving or opportunity, map the capabilities needed and define a small test with clear human controls.</p>
            <div className="conversion-hero__actions">
              <Link className="button button--gold" to="/request-demo">Find My Savings <ArrowRight size={18} /></Link>
              <Link className="button button--ghost" to="/request-demo?profile=business">Use DAWN for my business</Link>
            </div>
            <div className="conversion-hero__trust">
              <span><ShieldCheck size={17} />You approve important actions</span>
              <CircleDot size={5} />
              <span>Savings estimates are illustrative</span>
              <CircleDot size={5} />
              <span>No payment required to join</span>
            </div>
          </div>
          <PilotPreview />
        </div>
      </section>

      <section className="outcome-section section">
        <div className="container">
          <div className="outcome-section__intro">
            <h2>What does DAWN actually do?</h2>
            <p>It takes the repeatable parts of work that currently live in your head, inbox, notes and different apps, then turns them into clear jobs that can be prepared, checked and tracked in one place.</p>
          </div>
          <div className="outcome-rail">
            {outcomes.map(({ title, copy, icon: Icon }, index) => (
              <article key={title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <Icon size={24} />
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pilot-tracks section" id="simple-examples">
        <div className="container pilot-tracks__layout">
          <div className="pilot-tracks__intro">
            <h2>Common jobs DAWN can take off your plate.</h2>
            <p>You do not need to understand AI. Tell us what keeps taking too long, getting missed or pulling you away from customers. We turn one of those problems into a practical first workflow.</p>
            <Link className="text-link" to="/request-demo">Tell us what wastes your time <ArrowRight size={16} /></Link>
          </div>
          <div className="pilot-track-list">
            {helpAreas.map(({ number, title, copy, points, icon: Icon }) => (
              <article key={title}>
                <div className="pilot-track-list__heading">
                  <span>{number}</span>
                  <Icon size={24} />
                  <h3>{title}</h3>
                </div>
                <p>{copy}</p>
                <ul>
                  {points.map(point => <li key={point}><Check size={16} />{point}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="savings-section section">
        <div className="container">
          <SavingsEstimator />
        </div>
      </section>

      <section className="proof-section section">
        <div className="container proof-section__grid">
          <div className="proof-section__copy">
            <h2>We are using DAWN in our own business first.</h2>
            <p>Before asking customers to trust it, we are testing DAWN inside Deus Intus across marketing, products, website work, customer enquiries and day-to-day business management.</p>
            <p>Lower-risk jobs can prepare information and drafts. Anything involving sending, publishing, spending money or making a major change stays locked until a person approves it.</p>
            <Link className="button button--outline" to="/platform">See how it works <ArrowRight size={17} /></Link>
          </div>
          <div className="proof-section__evidence">
            <div className="proof-section__sun"><BrandMark compact light /></div>
            <article><span>15</span><div><strong>Types of recurring work mapped</strong><small>Including marketing, products, website checks, customer support and business oversight</small></div></article>
            <article><span>9</span><div><strong>Lower-risk workflows being tested</strong><small>Focused on preparation, checking, reminders and evidence</small></div></article>
            <article><span>6</span><div><strong>Higher-risk jobs kept locked</strong><small>They stay off until access, checks and approval rules are ready</small></div></article>
            <article><span>0</span><div><strong>Actions sent without approval</strong><small>No unapproved messages, publishing, spending or destructive changes</small></div></article>
          </div>
        </div>
      </section>

      <section className="operating-model section" id="how-it-works">
        <div className="container">
          <div className="operating-model__intro">
            <h2>Start small. Prove the saving. Then expand.</h2>
            <p>You do not buy a huge AI transformation. We begin with one job that is costing time or money and test whether DAWN genuinely improves it.</p>
          </div>
          <div className="operating-model__steps">
            {[
              ['01', 'Show us the problem', 'Tell us what keeps getting delayed, repeated, missed or left to you.'],
              ['02', 'Choose what DAWN can use', 'You approve the information and tools needed for that one job.'],
              ['03', 'Test one workflow', 'DAWN prepares or tracks the work while important actions remain with you.'],
              ['04', 'Measure the result', 'We compare time, cost and quality before deciding whether to expand.'],
            ].map(([number, title, copy]) => (
              <article key={title}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="control-section section">
        <div className="container control-section__grid">
          <div>
            <h2>You remain the boss.</h2>
            <p>DAWN is designed to help your team, not quietly take over. You choose what it can access, what it can prepare and what always needs approval.</p>
          </div>
          <div className="control-section__rail">
            <article><FileCheck2 size={22} /><div><strong>Nothing important happens unnoticed</strong><p>Messages, publishing, spending and major changes stay behind your approval.</p></div></article>
            <article><ShieldCheck size={22} /><div><strong>Only the access it needs</strong><p>Each job is limited to the information and permissions you agree to.</p></div></article>
            <article><Gauge size={22} /><div><strong>Clear limits</strong><p>Set budgets, pause work and stop a workflow whenever you choose.</p></div></article>
            <article><Workflow size={22} /><div><strong>A visible record</strong><p>See what was prepared, what changed and who approved it.</p></div></article>
          </div>
        </div>
      </section>

      <section className="pilot-cta section">
        <div className="container pilot-cta__inner">
          <div>
            <h2>What could DAWN take off your plate?</h2>
            <p>Tell us which work is slowing the business down. We will explain—in ordinary English—whether DAWN can help, what a first test would involve and how the result would be measured.</p>
          </div>
          <div className="pilot-cta__actions">
            <Link className="button button--gold" to="/request-demo">Join the first test cohort <ArrowRight size={18} /></Link>
            <Link className="text-link" to="/solutions">See more examples</Link>
          </div>
        </div>
      </section>
    </>
  )
}
